import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Row, 
  Col,
  message,
  DatePicker,
  Statistic,
  Popconfirm
} from 'antd';
import { 
  DollarOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ExportOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { mockProfitRecords, generateRandomProfitRecords, mockUsers, roleMap } from '../services/mockData';
import type { ProfitRecord, ProfitStatus, ProfitType } from '../types';
import dayjs from 'dayjs';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const ProfitManagement: React.FC = () => {
  const [profitRecords, setProfitRecords] = useState<ProfitRecord[]>(mockProfitRecords);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ProfitRecord | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProfitType | undefined>();
  const [statusFilter, setStatusFilter] = useState<ProfitStatus | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const typeMap: Record<ProfitType, string> = {
    basic: '基础分润',
    bonus: '奖励分润',
    management: '管理分润',
    regional: '区域分润',
  };

  const statusMap: Record<ProfitStatus, string> = {
    pending: '待确认',
    confirmed: '已确认',
    paid: '已支付',
  };

  const statusColors: Record<ProfitStatus, string> = {
    pending: 'warning',
    confirmed: 'processing',
    paid: 'success',
  };

  const statusIcons: Record<ProfitStatus, any> = {
    pending: <ClockCircleOutlined />,
    confirmed: <ExclamationCircleOutlined />,
    paid: <CheckOutlined />,
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: number) => {
        const user = mockUsers.find(u => u.id === userId);
        return user ? user.nickname : `用户${userId}`;
      },
    },
    {
      title: '分润类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: ProfitType) => {
        const colors: Record<ProfitType, string> = {
          basic: 'blue',
          bonus: 'green',
          management: 'orange',
          regional: 'purple',
        };
        return <Tag color={colors[type]}>{typeMap[type]}</Tag>;
      },
    },
    {
      title: '周期类型',
      dataIndex: 'periodType',
      key: 'periodType',
      render: (type: string) => {
        const periodMap: Record<string, string> = {
          daily: '日度',
          weekly: '周度',
          monthly: '月度',
          quarterly: '季度',
          yearly: '年度',
        };
        return periodMap[type] || type;
      },
    },
    {
      title: '基础金额',
      dataIndex: 'baseAmount',
      key: 'baseAmount',
      render: (amount: number) => 
        amount ? `¥${amount.toLocaleString()}` : '-',
    },
    {
      title: '分润比例',
      dataIndex: 'rate',
      key: 'rate',
      render: (rate: number) => rate ? `${(rate * 100).toFixed(2)}%` : '-',
    },
    {
      title: '分润金额',
      dataIndex: 'profitAmount',
      key: 'profitAmount',
      render: (amount: number) => `¥${amount.toLocaleString()}`,
    },
    {
      title: '奖励金额',
      dataIndex: 'bonusAmount',
      key: 'bonusAmount',
      render: (amount: number) => amount ? `¥${amount.toLocaleString()}` : '-',
    },
    {
      title: '总金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => `¥${amount.toLocaleString()}`,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: ProfitStatus) => (
        <Tag color={statusColors[status]} icon={statusIcons[status]}>
          {statusMap[status]}
        </Tag>
      ),
    },
    {
      title: '周期',
      key: 'period',
      render: (_: any, record: ProfitRecord) => (
        <div>
          <div>{record.periodStart}</div>
          <div>至</div>
          <div>{record.periodEnd}</div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_: any, record: ProfitRecord) => (
        <Space size="middle">
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {record.status === 'pending' && (
            <Button 
              type="link" 
              onClick={() => handleConfirm(record)}
            >
              确认
            </Button>
          )}
          {record.status === 'confirmed' && (
            <Button 
              type="link" 
              onClick={() => handlePay(record)}
            >
              支付
            </Button>
          )}
          <Popconfirm
            title="确定要删除这条分润记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              danger 
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const filteredRecords = profitRecords.filter(record => {
    const user = mockUsers.find(u => u.id === record.userId);
    const matchesSearch = !searchText || 
      user?.nickname?.toLowerCase().includes(searchText.toLowerCase());
    const matchesType = !typeFilter || record.type === typeFilter;
    const matchesStatus = !statusFilter || record.status === statusFilter;
    const matchesDate = !dateRange || (
      dayjs(record.createdAt).isAfter(dateRange[0]) &&
      dayjs(record.createdAt).isBefore(dateRange[1])
    );
    return matchesSearch && matchesType && matchesStatus && matchesDate;
  });

  const totalPending = profitRecords
    .filter(r => r.status === 'pending')
    .reduce((sum, r) => sum + r.totalAmount, 0);
  
  const totalConfirmed = profitRecords
    .filter(r => r.status === 'confirmed')
    .reduce((sum, r) => sum + r.totalAmount, 0);
  
  const totalPaid = profitRecords
    .filter(r => r.status === 'paid')
    .reduce((sum, r) => sum + r.totalAmount, 0);

  const handleAdd = () => {
    setEditingRecord(null);
    setModalVisible(true);
    form.resetFields();
  };

  const handleEdit = (record: ProfitRecord) => {
    setEditingRecord(record);
    setModalVisible(true);
    form.setFieldsValue({
      ...record,
      periodRange: [dayjs(record.periodStart), dayjs(record.periodEnd)],
    });
  };

  const handleDelete = (id: number) => {
    setProfitRecords(profitRecords.filter(record => record.id !== id));
    message.success('分润记录删除成功');
  };

  const handleConfirm = (record: ProfitRecord) => {
    setProfitRecords(profitRecords.map(r => 
      r.id === record.id 
        ? { ...r, status: 'confirmed', updatedAt: new Date().toISOString() }
        : r
    ));
    message.success('分润记录已确认');
  };

  const handlePay = (record: ProfitRecord) => {
    setProfitRecords(profitRecords.map(r => 
      r.id === record.id 
        ? { ...r, status: 'paid', updatedAt: new Date().toISOString() }
        : r
    ));
    message.success('分润记录已支付');
  };

  const handleModalOk = () => {
    form.validateFields().then(values => {
      const { periodRange, ...otherValues } = values;
      
      if (editingRecord) {
        // 编辑记录
        setProfitRecords(profitRecords.map(record => 
          record.id === editingRecord.id 
            ? { 
                ...record, 
                ...otherValues,
                periodStart: periodRange[0].format('YYYY-MM-DD'),
                periodEnd: periodRange[1].format('YYYY-MM-DD'),
                updatedAt: new Date().toISOString() 
              }
            : record
        ));
        message.success('分润记录更新成功');
      } else {
        // 新增记录
        const newRecord: ProfitRecord = {
          id: Date.now(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          ...otherValues,
          periodStart: periodRange[0].format('YYYY-MM-DD'),
          periodEnd: periodRange[1].format('YYYY-MM-DD'),
        };
        setProfitRecords([...profitRecords, newRecord]);
        message.success('分润记录添加成功');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const handleGenerateData = () => {
    setLoading(true);
    setTimeout(() => {
      const newRecords = generateRandomProfitRecords(20);
      setProfitRecords([...profitRecords, ...newRecords]);
      setLoading(false);
      message.success('成功生成20条模拟数据');
    }, 1000);
  };

  const handleExport = () => {
    message.info('导出功能开发中...');
  };

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="待确认金额"
              value={totalPending}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#faad14' }}
              suffix="元"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="已确认金额"
              value={totalConfirmed}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
              suffix="元"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="已支付金额"
              value={totalPaid}
              prefix={<CheckOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix="元"
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={6}>
              <Search
                placeholder="搜索用户"
                onSearch={setSearchText}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="筛选类型"
                allowClear
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: '100%' }}
              >
                <Option value="basic">基础分润</Option>
                <Option value="bonus">奖励分润</Option>
                <Option value="management">管理分润</Option>
                <Option value="regional">区域分润</Option>
              </Select>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Select
                placeholder="筛选状态"
                allowClear
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value="pending">待确认</Option>
                <Option value="confirmed">已确认</Option>
                <Option value="paid">已支付</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                placeholder={['开始日期', '结束日期']}
                onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={24} md={4}>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                  新增记录
                </Button>
                <Button icon={<SearchOutlined />} onClick={handleGenerateData}>
                  生成数据
                </Button>
                <Button icon={<ExportOutlined />} onClick={handleExport}>
                  导出
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredRecords}
          rowKey="id"
          pagination={{
            total: filteredRecords.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          loading={loading}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑分润记录' : '新增分润记录'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: 'basic',
            periodType: 'monthly',
            status: 'pending',
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="userId"
                label="用户ID"
                rules={[{ required: true, message: '请输入用户ID' }]}
              >
                <Input type="number" placeholder="请输入用户ID" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="分润类型"
                rules={[{ required: true, message: '请选择分润类型' }]}
              >
                <Select placeholder="请选择分润类型">
                  <Option value="basic">基础分润</Option>
                  <Option value="bonus">奖励分润</Option>
                  <Option value="management">管理分润</Option>
                  <Option value="regional">区域分润</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="periodType"
                label="周期类型"
                rules={[{ required: true, message: '请选择周期类型' }]}
              >
                <Select placeholder="请选择周期类型">
                  <Option value="daily">日度</Option>
                  <Option value="weekly">周度</Option>
                  <Option value="monthly">月度</Option>
                  <Option value="quarterly">季度</Option>
                  <Option value="yearly">年度</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select placeholder="请选择状态">
                  <Option value="pending">待确认</Option>
                  <Option value="confirmed">已确认</Option>
                  <Option value="paid">已支付</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="baseAmount"
                label="基础金额"
                rules={[{ required: true, message: '请输入基础金额' }]}
              >
                <Input type="number" placeholder="请输入基础金额" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="rate"
                label="分润比例"
                rules={[{ required: true, message: '请输入分润比例' }]}
              >
                <Input type="number" step="0.0001" placeholder="请输入分润比例" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="profitAmount"
                label="分润金额"
                rules={[{ required: true, message: '请输入分润金额' }]}
              >
                <Input type="number" placeholder="请输入分润金额" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="bonusAmount"
                label="奖励金额"
              >
                <Input type="number" placeholder="请输入奖励金额" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="periodRange"
            label="周期范围"
            rules={[{ required: true, message: '请选择周期范围' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProfitManagement;
